import { Fragment } from 'react';

import { faPlus, faTimes } from '@fortawesome/pro-light-svg-icons';
import {
  differenceInMinutes,
  isValid,
  setDate,
  setHours,
  setMinutes,
  startOfDay
} from 'date-fns';
import {
  DutyHourType,
  DutyHourTypeDynFlag,
  DutyCodePeriodRequest
} from 'generated';
import { chunk } from 'lodash';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import {
  fromISOString,
  getHoursMinutes,
  TimePeriod,
  toLocalISOString
} from 'utils/date';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';

import { FontAwesomeSvgIcon, TimePicker } from 'components/shared';
import DutyHourTypeSelector from 'components/shared/DutyHourTypeSelector';

type CreatePeriodsProps = {
  periodStartDate: Date;
  timePeriods: TimePeriod[];
  dutyHourTypeId?: number;
  tempId?: string;
};

type DutyCodePeriod = DutyCodePeriodRequest & {
  tempId?: string;
};

export const createPeriods = ({
  periodStartDate,
  timePeriods,
  dutyHourTypeId,
  tempId
}: CreatePeriodsProps) =>
  chunk(timePeriods, 2).map(
    (interval) =>
      ({
        startTime: toLocalISOString(
          setHours(
            setMinutes(
              periodStartDate,
              parseInt(interval[0].split(':')[1], 10)
            ),
            parseInt(interval[0].split(':')[0], 10)
          )
        ),
        endTime: toLocalISOString(
          setHours(
            setMinutes(
              periodStartDate,
              parseInt(interval[1].split(':')[1], 10)
            ),
            parseInt(interval[1].split(':')[0], 10)
          )
        ),
        tempId: tempId || uuidv4(),
        dutyHourTypeId: dutyHourTypeId
      }) as DutyCodePeriod
  );

type Props = {
  periods: DutyCodePeriodRequest[];
  hasAddPeriods?: boolean;
  hasDHTSelector?: boolean;
  hideBreakDuration?: boolean;
  dutyHourTypeDynFlags?: DutyHourTypeDynFlag[];
  onPeriodsChanged: (periods: DutyCodePeriodRequest[]) => void;
  referenceDate?: Date;
};

export default function TimePeriods({
  periods,
  hasAddPeriods = true,
  hasDHTSelector,
  hideBreakDuration,
  dutyHourTypeDynFlags,
  onPeriodsChanged,
  referenceDate
}: Props) {
  const { t } = useTranslation('common');

  const timePeriods = periods as DutyCodePeriod[];

  const updatePeriodDate = (
    periodIndex: number,
    time: 'startTime' | 'endTime',
    date: Date
  ) => {
    onPeriodsChanged([
      ...timePeriods.slice(0, periodIndex),
      {
        ...timePeriods[periodIndex],
        [time]: toLocalISOString(date)
      },
      ...timePeriods.slice(periodIndex + 1)
    ]);
  };

  const updatePeriodDHT = (periodIndex: number, selectedDHT: DutyHourType) => {
    onPeriodsChanged([
      ...timePeriods.slice(0, periodIndex),
      {
        ...timePeriods[periodIndex],
        dutyHourTypeId: selectedDHT.dutyHourTypeId
      },
      ...timePeriods.slice(periodIndex + 1)
    ]);
  };

  const addPeriods = () => {
    onPeriodsChanged([
      ...timePeriods,
      createPeriods({
        periodStartDate: startOfDay(
          fromISOString(timePeriods[timePeriods.length - 1].endTime)
        ),
        tempId: uuidv4(),
        timePeriods: [
          getHoursMinutes(
            timePeriods[timePeriods.length - 1].endTime
          ) as TimePeriod,
          getHoursMinutes(
            timePeriods[timePeriods.length - 1].endTime
          ) as TimePeriod
        ]
      })[0]
    ]);
  };

  const removePeriod = (periodIndex) => {
    onPeriodsChanged(timePeriods.filter((p, i) => i !== periodIndex));
  };

  const onEndTimeChanged = (endDate: Date, index: number) => {
    const period = timePeriods[index];
    const startDate = fromISOString(period.startTime);
    updatePeriodDate(
      index,
      'endTime',
      setDate(
        endDate,
        endDate.getHours() < startDate.getHours()
          ? startDate.getDate() + 1
          : startDate.getDate()
      )
    );
  };

  const totalHours = timePeriods.reduce((acc, p) => {
    acc +=
      differenceInMinutes(
        fromISOString(p.endTime),
        fromISOString(p.startTime)
      ) / 60;
    return acc;
  }, 0);
  return (
    <Grid container spacing={2}>
      {timePeriods.map((period, i) => {
        period.tempId = period.tempId || uuidv4();
        return (
          <Fragment key={period.dutyCodePeriodId || period.tempId}>
            <Grid item xs={hasDHTSelector ? 2 : 5}>
              <TimePicker
                value={fromISOString(period.startTime)}
                onChange={(date) =>
                  date &&
                  isValid(date) &&
                  updatePeriodDate(i, 'startTime', date)
                }
                label={t('startTime')}
              />
            </Grid>
            <Grid item xs={hasDHTSelector ? 2 : 5}>
              <TimePicker
                value={fromISOString(period.endTime)}
                onChange={(date) =>
                  date && isValid(date) && onEndTimeChanged(date, i)
                }
                label={t('endTime')}
              />
            </Grid>
            {hasDHTSelector && (
              <Grid item xs={7}>
                <DutyHourTypeSelector
                  onChange={(e, value) =>
                    value && updatePeriodDHT(i, value as DutyHourType)
                  }
                  label={t('dutyHourType')}
                  required
                  fullWidth
                  value={period.dutyHourTypeId}
                  filterDutyHourTypeDynFlags={dutyHourTypeDynFlags}
                  referenceDate={referenceDate}
                />
              </Grid>
            )}
            <Grid
              item
              xs={hasDHTSelector ? 1 : 2}
              className="d-flex v-bottom end"
            >
              <IconButton
                onClick={() => removePeriod(i)}
                color="primary"
                size="small"
                disabled={i === 0}
              >
                <FontAwesomeSvgIcon icon={faTimes} fontSize="small" />
              </IconButton>
            </Grid>
            {!hideBreakDuration && i < timePeriods.length - 1 && (
              <Grid item xs={12}>
                {differenceInMinutes(
                  fromISOString(timePeriods[i + 1].startTime),
                  fromISOString(period.endTime)
                )}{' '}
                m {t('pauseBreak')}
              </Grid>
            )}
            {hideBreakDuration && <Grid item xs={12} />}
          </Fragment>
        );
      })}
      {totalHours > 24 && (
        <Grid item xs={12}>
          <Alert severity="warning">{t('periodsExceeds24Hours')}</Alert>
        </Grid>
      )}
      {hasAddPeriods && (
        <Grid item xs={12}>
          <Button
            variant="outlined"
            size="small"
            onClick={addPeriods}
            startIcon={<FontAwesomeSvgIcon icon={faPlus} fontSize="small" />}
            disabled={totalHours > 24}
          >
            {t('addTimes')}
          </Button>
        </Grid>
      )}
    </Grid>
  );
}
